import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Stack,
  Tooltip
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot
} from '@mui/lab';
import {
  Assignment as AssignmentIcon,
  PlayArrow as StartIcon,
  CheckCircle as CompleteIcon,
  Notes as NotesIcon,
  PhotoCamera as PhotoIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Refresh as RefreshIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { workOrdersApi } from '../../services/api';

const getActivityIcon = (activityType) => {
  switch (activityType) {
    case 'created':
    case 'work_order_created':
      return <AssignmentIcon />;
    case 'status_update':
      return <StartIcon />;
    case 'note_added':
      return <NotesIcon />;
    case 'photo_uploaded':
      return <PhotoIcon />;
    default:
      return <TimeIcon />;
  }
};

const getActivityColor = (activityType, status) => {
  switch (activityType) {
    case 'created':
    case 'work_order_created':
      return 'info';
    case 'status_update':
      if (status === 'completed') return 'success';
      if (status === 'in_progress') return 'warning';
      return 'info';
    case 'note_added':
      return 'primary';
    case 'photo_uploaded':
      return 'secondary';
    default:
      return 'primary';
  }
};

const getPriorityColor = (priority) => {
  switch (priority) {
    case 'critical':
      return 'error';
    case 'high':
      return 'warning';
    case 'normal':
      return 'info';
    case 'low':
      return 'default';
    default:
      return 'default';
  }
};

const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMinutes = Math.floor((now - date) / (1000 * 60));
  
  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays}d ago`;
  
  return date.toLocaleDateString();
};

const WorkOrderTimelineWidget = ({ maxItems = 10, showHeader = true, compact = false }) => {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTimeline();
  }, [maxItems]);

  const fetchTimeline = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await workOrdersApi.getTimeline({ limit: maxItems });
      setTimeline(response.data.data); // ✅ FIXED: Access the inner data array
    } catch (error) {
      console.error('Failed to fetch timeline:', error);
      setError('Failed to load timeline. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchTimeline();
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={32} sx={{ mr: 2 }} />
            <Typography variant="body2">Loading timeline...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error" action={
            <Button size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }>
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        {showHeader && (
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6">
              Recent Activity
            </Typography>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Refresh
            </Button>
          </Stack>
        )}

        {timeline.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No recent activity
            </Typography>
          </Box>
        ) : (
          <Timeline sx={{ 
            [`& .MuiTimelineItem-root:before`]: {
              flex: 0,
              padding: 0,
            },
            px: 0
          }}>
            {timeline.map((item, index) => (
              <TimelineItem key={index}>
                <TimelineSeparator>
                  <TimelineDot 
                    color={getActivityColor(item.activity_type, item.status)}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      width: compact ? 32 : 40,
                      height: compact ? 32 : 40
                    }}
                  >
                    <Box sx={{ fontSize: compact ? 16 : 20 }}>
                      {getActivityIcon(item.activity_type)}
                    </Box>
                  </TimelineDot>
                  {index < timeline.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                
                <TimelineContent sx={{ py: compact ? 0.5 : 1 }}>
                  <Box sx={{ mb: 1 }}>
                    <Typography 
                      variant={compact ? "body2" : "body1"} 
                      sx={{ fontWeight: 500, mb: 0.5 }}
                    >
                      {item.description}
                    </Typography>
                    
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Chip
                        label={item.work_order_number}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                      
                      <Chip
                        label={item.priority}
                        size="small"
                        color={getPriorityColor(item.priority)}
                        sx={{ fontSize: '0.75rem' }}
                      />
                      
                      {item.assignee_name && (
                        <Tooltip title={`Assigned to ${item.assignee_name}`}>
                          <Chip
                            avatar={
                              <Avatar sx={{ width: 16, height: 16 }}>
                                {item.assignment_type === 'external' ? 
                                  <BusinessIcon sx={{ fontSize: 12 }} /> : 
                                  <PersonIcon sx={{ fontSize: 12 }} />
                                }
                              </Avatar>
                            }
                            label={item.assignee_name}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.75rem', maxWidth: 120 }}
                          />
                        </Tooltip>
                      )}
                    </Stack>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        by {item.actor_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        •
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatTimeAgo(item.event_time)}
                      </Typography>
                    </Box>
                  </Box>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        )}
      </CardContent>
    </Card>
  );
};

export default WorkOrderTimelineWidget; 